import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { EntityStatus } from "../types";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";
import DeveloperGuideSectionEditor from "./developer-guide/DeveloperGuideSectionEditor";
import DeveloperGuideRelationsPicker, {
  relationsSelectionToGuideUuids,
  type DeveloperGuideRelationsSelection,
} from "./developer-guide/DeveloperGuideRelationsPicker";
import { EnhanceWithAiProvider } from "./developer-guide/EnhanceWithAiDrawer";
import { useDeveloperGuideAgentChat } from "../hooks/useDeveloperGuideAgentChat";
import type { DeveloperGuideSection } from "../types";

import { PM_HUBS } from "../utils/pmHubRoutes";

interface AddDeveloperGuideProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddDeveloperGuide: React.FC<AddDeveloperGuideProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(
      returnPath
        ? resolveReturnDestination(returnPath, returnSearch)
        : PM_HUBS.developerGuide,
    );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);
  const [sections, setSections] = useState<DeveloperGuideSection[]>([
    {
      id: crypto.randomUUID(),
      sortOrder: 0,
      title: "Getting started",
      instructions: "",
      codeSamples: [],
    },
  ]);
  const [relations, setRelations] = useState<DeveloperGuideRelationsSelection>({
    apis: [],
    products: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { sendMessage, loading: aiLoading } = useDeveloperGuideAgentChat(tenantId);

  const handleEnhanceOverview = async () => {
    try {
      const response = await sendMessage(
        `Improve the name and description for a developer guide. Current name: "${name}". Current description: "${description}". Return a developer-guide-section JSON with title and instructions fields for the overview.`,
      );
      const titleMatch = response.match(/"title"\s*:\s*"([^"]+)"/);
      const descMatch = response.match(/"instructions"\s*:\s*"([^"]+)"/);
      if (titleMatch) setName(titleMatch[1]);
      if (descMatch) setDescription(descMatch[1].replace(/\\n/g, "\n"));
    } catch {
      /* error surfaced by hook */
    }
  };

  const handleAdd = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createDeveloperGuide({
        tenantId,
        name,
        description,
        status,
        sections,
        ...relationsSelectionToGuideUuids(relations),
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create developer guide",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EnhanceWithAiProvider tenantId={tenantId}>
      <Box>
        <PageTitle
          pageTitle="Add Developer Guide"
          pageDescription="Create integration guides with sections, code samples, and API/product links."
        />

        <Paper sx={{ p: 3, maxWidth: 960, mx: "auto" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              size="small"
              startIcon={<AutoFixHighIcon />}
              disabled={aiLoading}
              onClick={handleEnhanceOverview}
            >
              Enhance overview
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Guide name"
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Status"
            margin="normal"
            value={status}
            onChange={(e) => setStatus(e.target.value as EntityStatus)}
          >
            {Object.values(EntityStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <DeveloperGuideRelationsPicker
            value={relations}
            onChange={setRelations}
          />

          <DeveloperGuideSectionEditor
            sections={sections}
            onChange={setSections}
            guideName={name}
            linkedApiUuids={relations.apis.map((a) => a.uuid)}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={goBack}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={submitting || !name.trim()}
              onClick={handleAdd}
            >
              {submitting ? "Creating..." : "Create Developer Guide"}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          message="Developer guide created successfully"
        />
      </Box>
    </EnhanceWithAiProvider>
  );
};

export default AddDeveloperGuide;
